/**
 * Defensive normalisation for values coming out of `mail.get`.
 *
 * ⚠ These are BELT-AND-BRACES, not the real fix. The runtime's `mail.get`
 * currently (a) returns the Subject as a raw RFC 2047 encoded-word and
 * (b) prefixes `bodyText` with the RFC 5322 header block — both reported in
 * `handoffs/mail-list-heartbeat-and-decoding-lexruntime-handoff-2026-07-22.md`
 * §2/§3. Until that lands, a lawyer would otherwise read
 * `=?UTF-8?Q?...?=` and a wall of `From:/To:/Subject:` above every message.
 *
 * Both helpers are deliberately IDEMPOTENT and lossless-on-doubt:
 *  - text with no encoded-word comes back byte-identical, so they become
 *    no-ops the moment the runtime decodes server-side (no double-decoding,
 *    nothing to unwind);
 *  - anything that does not clearly match is returned VERBATIM rather than
 *    guessed at — mangling a real subject would be worse than the raw form.
 */

/** `utf-8*en` → `utf-8`; unknown/blank → utf-8 (TextDecoder's default). */
function normaliseCharset(charset: string): string {
  const base = charset.split("*")[0]?.trim().toLowerCase();
  return base || "utf-8";
}

function decodeQ(text: string): Uint8Array {
  // RFC 2047 "Q": `_` is a space, `=XX` is a hex octet.
  const q = text.replace(/_/g, " ");
  const out: number[] = [];
  for (let i = 0; i < q.length; i++) {
    if (q[i] === "=" && i + 2 < q.length) {
      const hex = q.slice(i + 1, i + 3);
      if (/^[0-9a-fA-F]{2}$/.test(hex)) {
        out.push(parseInt(hex, 16));
        i += 2;
        continue;
      }
    }
    out.push(q.charCodeAt(i) & 0xff);
  }
  return Uint8Array.from(out);
}

function decodeB(text: string): Uint8Array {
  const bin = atob(text);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

/**
 * Decode RFC 2047 encoded-words (`=?UTF-8?Q?...?=` / `=?UTF-8?B?...?=`) that a
 * raw header parse leaves in place. Input without `=?` is returned unchanged.
 */
export function decodeMimeWords(input: string): string {
  if (!input || !input.includes("=?")) return input;
  // Whitespace BETWEEN adjacent encoded-words is not data (RFC 2047 §6.2) —
  // a long subject is split into several words and must rejoin seamlessly.
  const joined = input.replace(/(\?=)\s+(=\?)/g, "$1$2");
  return joined.replace(
    /=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g,
    (whole, charset: string, enc: string, text: string) => {
      try {
        const bytes = enc.toUpperCase() === "B" ? decodeB(text) : decodeQ(text);
        return new TextDecoder(normaliseCharset(charset)).decode(bytes);
      } catch {
        // Undecodable (bad base64, unsupported charset) → keep it verbatim.
        return whole;
      }
    },
  );
}

/** A line that looks like `Header-Name: value`. */
const HEADER_LINE = /^[A-Za-z][A-Za-z0-9-]*:(\s|$)/;
/** A folded continuation of the previous header (leading whitespace). */
const FOLDED_LINE = /^[ \t]+\S/;

/**
 * Drop a leading RFC 5322 header block from a message body.
 *
 * Only strips when the body ACTUALLY starts with headers terminated by a blank
 * line — otherwise the body is returned untouched, so a message that merely
 * happens to begin with "Subject: ..." prose is never truncated.
 */
export function stripLeadingHeaderBlock(body: string): string {
  if (!body) return body;
  const lines = body.split(/\r?\n/);
  if (!HEADER_LINE.test(lines[0] ?? "")) return body;

  let i = 0;
  let sawBlank = false;
  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (line.trim() === "") {
      sawBlank = true;
      i++;
      break;
    }
    if (HEADER_LINE.test(line) || FOLDED_LINE.test(line)) {
      i++;
      continue;
    }
    // A non-header line before the blank separator ⇒ this is not a header
    // block after all. Leave the body exactly as it came.
    return body;
  }
  if (!sawBlank) return body;
  return lines.slice(i).join("\n").replace(/^\n+/, "");
}
